/**
 * DataDog RUM SDK Configuration
 *
 * Initializes Real User Monitoring for Procright frontend application.
 * Includes session replay, user interaction tracking, and APM correlation.
 *
 * @see docs/rum-actions-map.md for full RUM action documentation
 */
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

/**
 * Feature flags interface for runtime context
 */
interface FeatureFlags {
  [key: string]: boolean | string | number;
}

/**
 * Get active feature flags from application state
 */
const getActiveFeatureFlags = (): FeatureFlags => {
  // TODO: Connect to your feature flag system (LaunchDarkly, Flagsmith, etc.)
  // For now, return empty object
  return {};
};

/**
 * Initialize DataDog RUM SDK
 *
 * Should be called ONCE at application startup, BEFORE any other code.
 * Import this module at the top of main.tsx
 */
export const initDatadogRUM = (): void => {
  // Skip initialization if credentials are not configured
  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const site = import.meta.env.VITE_DD_SITE || 'datadoghq.eu';

  if (!applicationId || !clientToken) {
    console.warn('[DataDog RUM] Missing VITE_DD_RUM_APPLICATION_ID or VITE_DD_RUM_CLIENT_TOKEN. RUM disabled.');
    return;
  }

  // Initialize RUM SDK
  datadogRum.init({
    applicationId,
    clientToken,
    site,  // Procright uses EU datacenter (from VITE_DD_SITE)
    service: 'procright-frontend',
    env: import.meta.env.VITE_NODE_ENV || 'development',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Session sampling
    sessionSampleRate: 100,  // Capture 100% of sessions
    sessionReplaySampleRate: 20,  // Capture 20% for session replay

    // Tracking options
    trackUserInteractions: true,  // Auto-track clicks, scrolls, etc.
    trackResources: true,  // Track XHR, fetch, static resources
    trackLongTasks: true,  // Track JavaScript long tasks (>50ms)
    defaultPrivacyLevel: 'mask-user-input',  // Mask sensitive user input

    // APM correlation - connect RUM sessions to backend traces
    allowedTracingUrls: [
      {
        match: /https:\/\/api(-test)?\.procright\.com/,
        propagatorTypes: ['tracecontext', 'datadog']
      },
      {
        match: /https:\/\/ai(-test)?\.procright\.com/,
        propagatorTypes: ['tracecontext', 'datadog']
      },
      // Local development
      {
        match: /http:\/\/localhost:3000/,
        propagatorTypes: ['tracecontext', 'datadog']
      }
    ],

    // Custom event processing
    beforeSend: (event) => {
      // Add global context to all events
      event.context = {
        ...event.context,
        deployment_region: 'eu-west-1',
        feature_flags: getActiveFeatureFlags(),
      };

      // Filter out noisy events in development
      if (import.meta.env.VITE_NODE_ENV === 'development') {
        // Skip tracking for hot-reload and dev tools
        if (event.type === 'resource' && event.resource?.url?.includes('hot-update')) {
          return false;
        }
      }

      return true;
    },
  });

  // Start session replay recording
  datadogRum.startSessionReplayRecording();

  console.info('[DataDog RUM] Initialized successfully', {
    env: import.meta.env.VITE_NODE_ENV,
    service: 'procright-frontend',
    site: 'datadoghq.eu',
  });
};

/**
 * Initialize DataDog Browser Logs SDK
 *
 * Should be called alongside RUM initialization for centralized logging.
 */
export const initDatadogLogs = (): void => {
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const site = import.meta.env.VITE_DD_SITE || 'datadoghq.eu';

  if (!clientToken) {
    console.warn('[DataDog Logs] Missing VITE_DD_RUM_CLIENT_TOKEN. Logs disabled.');
    return;
  }

  datadogLogs.init({
    clientToken,
    site,
    service: 'procright-frontend',
    env: import.meta.env.VITE_NODE_ENV || 'development',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Forward console logs to DataDog
    forwardConsoleLogs: ['error', 'warn'],  // Only forward errors and warnings
    forwardErrorsToLogs: true,  // Send uncaught errors to logs

    // Session sampling
    sessionSampleRate: 100,
  });

  console.info('[DataDog Logs] Initialized successfully');
};

/**
 * Set user context for RUM session
 *
 * Call this after successful login to associate sessions with users.
 *
 * @param user - User information to attach to RUM session
 */
export const setRUMUser = (user: {
  id: string;
  email?: string;
  name?: string;
  companyId?: string;
  role?: string;
  tier?: string;
}): void => {
  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
  });

  // Set additional user context as global properties
  if (user.companyId) {
    datadogRum.setGlobalContextProperty('user.company_id', user.companyId);
  }
  if (user.role) {
    datadogRum.setGlobalContextProperty('user.role', user.role);
  }
  if (user.tier) {
    datadogRum.setGlobalContextProperty('user.tier', user.tier);
  }
};

/**
 * Clear user context from RUM session
 *
 * Call this on logout to disassociate sessions from users.
 */
export const clearRUMUser = (): void => {
  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('user.company_id');
  datadogRum.removeGlobalContextProperty('user.role');
  datadogRum.removeGlobalContextProperty('user.tier');
};

/**
 * Add custom RUM action
 *
 * Wrapper around datadogRum.addAction with standard context.
 *
 * @param name - Action name (e.g., 'specification_created')
 * @param context - Custom attributes for the action
 */
export const trackRUMAction = (
  name: string,
  context?: Record<string, unknown>
): void => {
  datadogRum.addAction(name, {
    ...context,
    timestamp: Date.now(),
  });
};

/**
 * Add custom RUM error
 *
 * Wrapper around datadogRum.addError with standard context.
 *
 * @param error - Error object or message
 * @param context - Custom attributes for the error
 */
export const trackRUMError = (
  error: Error | string,
  context?: Record<string, unknown>
): void => {
  datadogRum.addError(error, {
    ...context,
    timestamp: Date.now(),
  });
};

/**
 * Initialize all DataDog SDKs
 *
 * Single entry point to initialize both RUM and Logs.
 * Call this at the very top of main.tsx
 */
export const initDatadog = (): void => {
  initDatadogRUM();
  initDatadogLogs();
};

// Re-export datadogRum for direct usage where needed
export { datadogRum };
